import { ModalViewportMetrics } from "./components/ui/modal-viewport";
import './components/ui/responsive-modal.css';
import React, { useEffect, lazy as reactLazy, Suspense } from 'react';
import axios from 'axios';
const API_URL = process.env.REACT_APP_BACKEND_URL;
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import { MainLayout } from './components/layout';
import { BrandProvider } from './components/BrandProvider';
import { ThemeProvider } from './components/ThemeProvider';
import { I18nProvider } from './components/I18nProvider';
// Sayfalar ihtiyaç anında yüklenir (code splitting) — bayi admin kodunu indirmez
// Yeni sürüm yayınlandığında eski parça bulunamazsa sayfayı bir kez yenile
const lazy = (loader) => reactLazy(() => loader().catch((err) => {
  if (!sessionStorage.getItem('chunk_reload')) {
    sessionStorage.setItem('chunk_reload', '1');
    window.location.reload();
    return new Promise(() => {});
  }
  throw err;
}).then((m) => { sessionStorage.removeItem('chunk_reload'); return m; }));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const CustomersPage = lazy(() => import('./pages/CustomersPage'));
const PaymentsPage = lazy(() => import('./pages/PaymentsPage'));
const CollectedPage = lazy(() => import('./pages/CollectedPage'));
const PersonnelPage = lazy(() => import('./pages/PersonnelPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const WhatsAppPage = lazy(() => import('./pages/WhatsAppPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const LegalPage = lazy(() => import('./pages/LegalPage'));
const LandingPage = lazy(() => import('./pages/LandingPage'));
const SuspendedPage = lazy(() => import('./pages/SuspendedPage'));
const TrialExpiredPage = lazy(() => import('./pages/TrialExpiredPage'));
const DealerApplicationPage = lazy(() => import('./pages/DealerApplicationPage'));
const AdminDealerApplicationsPage = lazy(() => import('./pages/admin/AdminDealerApplicationsPage'));
const AdminDealerActivityPage = lazy(() => import('./pages/admin/AdminDealerActivityPage'));
const AdminRevenueRewardsPage = lazy(() => import('./features/revenue-rewards/AdminRevenueRewardsPage'));
const AdminParaPuanPage = lazy(() => import('./pages/admin/AdminParaPuanPage'));
const AdminCampaignsPage = lazy(() => import('./pages/admin/AdminCampaignsPage'));
const PersonnelWhatsAppPage = lazy(() => import('./pages/PersonnelWhatsAppPage'));
const DealerPaymentPromiseNew = lazy(() => import('./pages/dealer/DealerPaymentPromiseNew'));
const DealerPaymentKT = lazy(() => import('./pages/dealer/DealerPaymentKT'));
const AdminDashboardPage = lazy(() => import('./pages/admin/AdminDashboardPage'));
const AdminCompaniesPage = lazy(() => import('./pages/admin/AdminCompaniesPage'));
const AdminUsersPage = lazy(() => import('./pages/admin/AdminUsersPage'));
const AdminMessagesPage = lazy(() => import('./pages/admin/AdminMessagesPage'));
const AdminSettingsPage = lazy(() => import('./pages/admin/AdminSettingsPage'));
const AdminTemplatesPage = lazy(() => import('./pages/admin/AdminTemplatesPage'));
const AdminPackagesPage = lazy(() => import('./pages/admin/AdminPackagesPage'));
const AdminPaymentMethodsPage = lazy(() => import('./pages/admin/AdminPaymentMethodsPage'));
const AdminWhatsAppPage = lazy(() => import('./pages/admin/AdminWhatsAppPage'));
const AdminSecurityPage = lazy(() => import('./pages/admin/AdminSecurityPage'));
const AdminProductsPage = lazy(() => import('./pages/admin/AdminProductsPage'));
const AdminOrdersPage = lazy(() => import('./pages/admin/AdminOrdersPage'));
const AdminDealersPage = lazy(() => import('./pages/admin/AdminDealersPage'));
const AdminIntegrationsPage = lazy(() => import('./pages/admin/AdminIntegrationsPage'));
const AdminPaymentTrackingPage = lazy(() => import('./pages/admin/AdminPaymentTrackingPage'));
const AdminPersonnelPage = lazy(() => import('./pages/admin/AdminPersonnelPage'));
const AdminShopSettingsPage = lazy(() => import('./pages/admin/AdminShopSettingsPage'));
const DealerDashboard = lazy(() => import('./pages/dealer/DealerDashboard'));
const DealerOrders = lazy(() => import('./pages/dealer/DealerOrders'));
const DealerCart = lazy(() => import('./pages/dealer/DealerCart'));
const DealerShop = lazy(() => import('./pages/dealer/DealerShop'));
const DealerPayments = lazy(() => import('./pages/dealer/DealerPayments'));
const DealerAccount = lazy(() => import('./pages/dealer/DealerAccount'));
const DealerParaPuanHistory = lazy(() => import('./pages/dealer/DealerParaPuanHistory'));
const PaymentSuccessPage = lazy(() => import('./pages/PaymentResultPages').then(m => ({ default: m.PaymentSuccessPage })));
const PaymentFailurePage = lazy(() => import('./pages/PaymentResultPages').then(m => ({ default: m.PaymentFailurePage })));
import { DealerLayout } from './components/layout/DealerLayout';
import { setImgVersion } from './lib/img';
import { useAuthStore } from './store';
import { useIdleLogout } from './hooks/useIdleLogout';
import { useTabSessionGuard } from './hooks/useTabSessionGuard';

function PageLoader() {
  return (
    <div className="min-h-[50vh] flex items-center justify-center" data-testid="page-loader">
      <div className="w-8 h-8 border-4 border-red-200 border-t-red-600 rounded-full animate-spin" />
    </div>
  );
}

function PaymentResultPage({ status }) {
  const navigate = useNavigate();
  React.useEffect(() => {
    const timer = setTimeout(() => navigate('/login'), 5000);
    return () => clearTimeout(timer);
  }, [navigate]);
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="text-center max-w-md space-y-4">
        {status === 'success' ? (
          <>
            <div className="w-16 h-16 mx-auto rounded-full bg-green-100 flex items-center justify-center">
              <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
            </div>
            <h1 className="text-2xl font-bold text-green-700">Ödeme Başarılı!</h1>
            <p className="text-muted-foreground">Ödemeniz başarıyla alınmıştır. Teşekkür ederiz! Aboneliğiniz aktif edildi.</p>
          </>
        ) : (
          <>
            <div className="w-16 h-16 mx-auto rounded-full bg-red-100 flex items-center justify-center">
              <svg className="w-8 h-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </div>
            <h1 className="text-2xl font-bold text-red-700">Ödeme Başarısız</h1>
            <p className="text-muted-foreground">Ödeme işlemi tamamlanamadı. Lütfen tekrar deneyin veya farklı bir kart kullanın.</p>
          </>
        )}
        <p className="text-sm text-muted-foreground">5 saniye sonra giriş sayfasına yönlendirileceksiniz...</p>
        <button onClick={() => navigate('/login')} className="text-primary font-medium hover:underline text-sm">Giriş Sayfasına Git</button>
      </div>
    </div>
  );
}
// Personnel subscription expired page
function SubscriptionExpiredPersonnelPage() {
  const { logout } = useAuthStore();
  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4" data-testid="subscription-expired-personnel">
      <div className="w-full max-w-md rounded-xl border bg-card p-8 text-center shadow-lg">
        <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-amber-100">
          <svg className="h-10 w-10 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
        </div>
        <h1 className="text-2xl font-bold text-amber-700">Abonelik Süresi Doldu</h1>
        <p className="mt-3 text-muted-foreground">
          Firmanızın abonelik süresi dolmuştur. Sistemi kullanmaya devam etmek için firma yöneticinizin abonelik yenilemesi gerekmektedir.
        </p>
        <div className="mt-6 rounded-lg bg-muted/50 p-4 text-sm text-muted-foreground">
          Lütfen firma yöneticinize başvurun.
        </div>
        <button onClick={logout} className="mt-6 w-full rounded-lg border bg-background px-4 py-2.5 text-sm font-medium hover:bg-muted transition-colors" data-testid="expired-logout-btn">
          Çıkış Yap
        </button>
      </div>
    </div>
  );
}

import './App.css';

// v58.99: Route değişiminde /admin'e gelindiğinde viewAsDealer'i temizle
// (browser back / mobile back sonrası admin endpoint'leri 403 dönmemesi için).
// SADECE pathname değişiminde çalışır — impersonate akışında (state set → navigate) tetiklenmez.
const ViewAsDealerGuard = () => {
  const location = useLocation();
  const clearViewAsDealer = useAuthStore((s) => s.clearViewAsDealer);
  useEffect(() => {
    if (location.pathname.startsWith('/admin')) {
      const cur = useAuthStore.getState().viewAsDealer;
      if (cur && cur.id) {
        clearViewAsDealer();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);
  return null;
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isSuspended, isTrialExpired, user } = useAuthStore();
  // v59.21: Oturum süresi backend'den çekilir (default 15 dk)
  const [idleMinutes, setIdleMinutes] = React.useState(15);
  React.useEffect(() => {
    if (!isAuthenticated) return;
    const controller = new AbortController();
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/shop-config`, {
          signal: controller.signal,
          headers: { Authorization: `Bearer ${useAuthStore.getState().token}` },
        });
        if (data?.session_idle_minutes) setIdleMinutes(parseInt(data.session_idle_minutes) || 15);
      } catch { /* ignore */ }
    })();
    return () => controller.abort();
  }, [isAuthenticated]);
  useIdleLogout(idleMinutes);
  useTabSessionGuard();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (isSuspended) {
    return <SuspendedPage />;
  }

  // Trial/subscription expired handling
  if (isTrialExpired) {
    // Personnel: show "subscription expired" message
    if (user?.role === 'personnel') {
      return <SubscriptionExpiredPersonnelPage />;
    }
    // Company admin: allow access but will be restricted to settings via sidebar
    // (handled in route guard below)
  }
  
  return children;
};

// Route guard for expired companies - only allow settings page
const ExpiredRouteGuard = ({ children, allowWhenExpired }) => {
  const { isTrialExpired, user } = useAuthStore();
  if (isTrialExpired && user?.role !== 'super_admin' && user?.role !== 'personnel' && !allowWhenExpired) {
    return <Navigate to="/settings" replace />;
  }
  return children;
};

// v58.91: Personel için "izin sahibi olduğu ilk sayfa" hesabı — dashboard yoksa bayiler/vs.
const _PERM_TO_PATH = [
  ['dashboard', '/admin'],
  ['dealers', '/admin/dealers'],
  ['orders', '/admin/orders'],
  ['products', '/admin/products'],
  ['payment_tracking', '/admin/payment-tracking'],
  ['whatsapp', '/admin/whatsapp'],
  ['integrations', '/admin/integrations'],
  ['security', '/admin/security'],
  ['settings', '/admin/settings'],
];
const firstAllowedAdminPath = (user) => {
  if (user?.role === 'super_admin') return '/admin';
  const perms = user?.permissions || [];
  for (const [p, path] of _PERM_TO_PATH) {
    if (perms.includes(p)) return path;
  }
  return '/admin/dealers';  // fallback (dealers her personelde varsayilanla acilabilir)
};

// Landing Route - show barcode search if not authenticated, redirect based on role if authenticated
const LandingRoute = () => {
  const { isAuthenticated, user } = useAuthStore();
  
  if (isAuthenticated) {
    if (user?.role === 'super_admin') {
      return <Navigate to="/admin" replace />;
    } else if (user?.role === 'personnel') {
      return <Navigate to={firstAllowedAdminPath(user)} replace />;
    } else if (user?.role === 'dealer') {
      return <Navigate to="/dealer" replace />;
    }
    // Bilinmeyen rol → login sayfasına (dashboard route'u kaldırıldı - Feb 2026)
    return <Navigate to="/login" replace />;
  }
  
  return <LandingPage />;
};

// Public Route Component (redirect if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, user } = useAuthStore();
  
  if (isAuthenticated) {
    if (user?.role === 'super_admin') return <Navigate to="/admin" replace />;
    if (user?.role === 'personnel') return <Navigate to={firstAllowedAdminPath(user)} replace />;
    if (user?.role === 'dealer') return <Navigate to="/dealer" replace />;
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Admin Route Guard
const AdminRoute = ({ children }) => {
  const { user } = useAuthStore();
  if (user?.role !== 'super_admin' && user?.role !== 'personnel') {
    // Bayi ise kendi paneline, aksi halde login'e
    if (user?.role === 'dealer') return <Navigate to="/dealer" replace />;
    return <Navigate to="/login" replace />;
  }
  // v58.91: Personel `dashboard` yetkisi yoksa ilk izinli sayfaya yonlendir
  if (user?.role === 'personnel') {
    const perms = user?.permissions || [];
    if (!perms.includes('dashboard')) {
      return <Navigate to={firstAllowedAdminPath(user)} replace />;
    }
  }
  return children;
};

// v60.0: /admin/whatsapp route — personel için kendi WA sayfası, admin için tüm yönetim paneli
const PersonnelOrAdminWhatsApp = () => {
  const { user } = useAuthStore();
  if (user?.role === 'personnel') return <PersonnelWhatsAppPage />;
  return <AdminWhatsAppPage />;
};

// Super Admin Only Route Guard
const SuperAdminRoute = ({ children }) => {
  const { user } = useAuthStore();
  if (user?.role !== 'super_admin') {
    return <Navigate to="/admin" replace />;
  }
  return children;
};

// v58.56: Personnel Permission Route Guard - belirli izne sahip degilse admin'e yonlendir
const PERMISSION_MAP = {
  '/admin/dealers': 'dealers',
  '/admin/products': 'products',
  '/admin/shop-settings': 'products',
  '/admin/orders': 'orders',
  '/admin/payment-tracking': 'payment_tracking',
  '/admin/whatsapp': 'whatsapp',
  '/admin/integrations': 'integrations',
  '/admin/security': 'security',
  '/admin/settings': 'settings',
};

const PermissionRoute = ({ children, permission }) => {
  const { user } = useAuthStore();
  if (user?.role === 'super_admin') return children;
  if (user?.role !== 'personnel') return <Navigate to="/" replace />;
  const perms = user?.permissions || [];
  if (permission && !perms.includes(permission)) {
    return <Navigate to="/admin" replace />;
  }
  return children;
};

// Dealer Route Guard
const DealerRoute = ({ children }) => {
  const { user, viewAsDealer } = useAuthStore();
  // Admin view-as modunda ise bayi sayfalarına erişebilir
  if (user?.role === 'dealer') return children;
  if (viewAsDealer && viewAsDealer.id && ['super_admin', 'admin', 'company_admin', 'personnel'].includes(user?.role)) {
    return children;
  }
  return <Navigate to="/" replace />;
};

// Admin "Önbelleği Temizle" → sürüm değişir → her kullanıcıda bir kez önbellek temizlenip sayfa yenilenir
function useAppCacheVersion() {
  useEffect(() => {
    axios.get(`${API_URL}/api/app-version`).then(async (r) => {
      const v = r.data?.version || '';
      if (!v) return;
      const stored = localStorage.getItem('app_cache_version');
      localStorage.setItem('app_cache_version', v);
      setImgVersion(v);
      if (stored && stored !== v) {
        try {
          if (window.caches) { const keys = await window.caches.keys(); await Promise.all(keys.map(k => window.caches.delete(k))); }
        } catch { /* ignore */ }
        window.location.reload();
      }
    }).catch(() => {});
  }, []);
}

function App() {
  useAppCacheVersion();
  return (
    <ThemeProvider>
    <I18nProvider>
    <BrowserRouter>
      <ModalViewportMetrics />
      <BrandProvider>
      <ViewAsDealerGuard />
      <Toaster 
        position="top-right" 
        richColors 
        closeButton
        toastOptions={{
          duration: 4000,
        }}
      />
      <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Landing Page */}
        <Route path="/" element={<LandingRoute />} />
        
        {/* Public Routes */}
        <Route
          path="/login"
          element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          }
        />
        <Route path="/register" element={<Navigate to="/login" replace />} />
        <Route path="/bayi-ol" element={<DealerApplicationPage />} />
        <Route path="/dealer-application" element={<Navigate to="/bayi-ol" replace />} />
        <Route path="/legal/:tab" element={<LegalPage />} />
        <Route path="/legal" element={<LegalPage />} />

        {/* Protected Routes with Layout */}
        <Route
          element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          }
        >
          {/* Legacy generic routes KALDIRILDI (Feb 2026) - kullanıcı isteği: /dashboard, /customers,
              /whatsapp, /personnel, /settings, /profile eski SaaS layout'unu açıyordu.
              Merve Yayınevi B2B portalında bu sayfalara ihtiyaç yok. Legacy trafiği admin paneline yönlendir. */}
          <Route path="dashboard" element={<Navigate to="/admin" replace />} />
          <Route path="customers" element={<Navigate to="/admin/dealers" replace />} />
          <Route path="payments" element={<Navigate to="/admin/payment-tracking" replace />} />
          <Route path="collected" element={<Navigate to="/admin/payment-tracking" replace />} />
          <Route path="whatsapp" element={<Navigate to="/admin/whatsapp" replace />} />
          <Route path="personnel" element={<Navigate to="/admin/personnel" replace />} />
          <Route path="settings" element={<Navigate to="/admin/settings" replace />} />
          <Route path="profile" element={<Navigate to="/admin" replace />} />

          {/* Admin Routes */}
          <Route path="admin" element={<AdminRoute><AdminDashboardPage /></AdminRoute>} />
          <Route path="admin/dealers" element={<PermissionRoute permission="dealers"><AdminDealersPage /></PermissionRoute>} />
          <Route path="admin/dealer-applications" element={<PermissionRoute permission="dealers"><AdminDealerApplicationsPage /></PermissionRoute>} />
          <Route path="admin/dealer-activity" element={<PermissionRoute permission="dealer_activity"><AdminDealerActivityPage /></PermissionRoute>} />
          <Route path="admin/para-puan" element={<PermissionRoute permission="para_puan"><AdminParaPuanPage /></PermissionRoute>} />
          <Route path="admin/products" element={<PermissionRoute permission="products"><AdminProductsPage /></PermissionRoute>} />
          <Route path="admin/campaigns" element={<PermissionRoute permission="products"><AdminCampaignsPage /></PermissionRoute>} />
          <Route path="admin/orders" element={<PermissionRoute permission="orders"><AdminOrdersPage /></PermissionRoute>} />
          <Route path="admin/payment-tracking" element={<PermissionRoute permission="payment_tracking"><AdminPaymentTrackingPage /></PermissionRoute>} />
          <Route path="admin/ciro-hedefleri/*" element={<SuperAdminRoute><AdminRevenueRewardsPage /></SuperAdminRoute>} />
          <Route path="admin/whatsapp" element={<PermissionRoute permission="whatsapp"><PersonnelOrAdminWhatsApp /></PermissionRoute>} />
          <Route path="admin/personnel" element={<SuperAdminRoute><AdminPersonnelPage /></SuperAdminRoute>} />
          <Route path="admin/integrations" element={<PermissionRoute permission="integrations"><AdminIntegrationsPage /></PermissionRoute>} />
          <Route path="admin/security" element={<PermissionRoute permission="security"><AdminSecurityPage /></PermissionRoute>} />
          <Route path="admin/settings" element={<PermissionRoute permission="settings"><AdminSettingsPage /></PermissionRoute>} />
          <Route path="admin/shop-settings" element={<PermissionRoute permission="products"><AdminShopSettingsPage /></PermissionRoute>} />
          {/* Legacy redirects */}
          <Route path="admin/companies" element={<Navigate to="/admin" replace />} />
          <Route path="admin/packages" element={<Navigate to="/admin" replace />} />
          <Route path="admin/users" element={<Navigate to="/admin/dealers" replace />} />
          <Route path="admin/messages" element={<Navigate to="/admin/whatsapp" replace />} />
          <Route path="admin/templates" element={<Navigate to="/admin/whatsapp" replace />} />
          <Route path="admin/payment-methods" element={<Navigate to="/admin/integrations" replace />} />
        </Route>

        {/* Dealer Routes with DealerLayout */}
        <Route
          element={
            <ProtectedRoute>
              <DealerRoute>
                <DealerLayout />
              </DealerRoute>
            </ProtectedRoute>
          }
        >
          <Route path="dealer" element={<DealerDashboard />} />
          <Route path="dealer/shop" element={<DealerShop />} />
          <Route path="dealer/cart" element={<DealerCart />} />
          <Route path="dealer/orders" element={<DealerOrders />} />
          <Route path="dealer/para-puan" element={<DealerParaPuanHistory />} />
          <Route path="dealer/para-puan/history" element={<DealerParaPuanHistory />} />
          <Route path="dealer/payments" element={<DealerPayments />} />
          <Route path="dealer/payment-promise/new" element={<DealerPaymentPromiseNew />} />
          <Route path="dealer/payment/:orderId" element={<DealerPaymentKT />} />
          <Route path="dealer/payment-cari" element={<DealerPaymentKT />} />
          <Route path="payment/success" element={<PaymentSuccessPage />} />
          <Route path="payment/failure" element={<PaymentFailurePage />} />
          <Route path="dealer/account" element={<DealerAccount />} />
          <Route path="dealer/profile" element={<ProfilePage />} />
        </Route>

        {/* Payment Result Pages */}
        <Route path="payment/success" element={<PaymentResultPage status="success" />} />
        <Route path="payment/fail" element={<PaymentResultPage status="fail" />} />

        {/* Catch all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </Suspense>
      </BrandProvider>
    </BrowserRouter>
    </I18nProvider>
    </ThemeProvider>
  );
}

export default App;
